import {
    computed,
    defineComponent,
    onBeforeUnmount,
    PropType,
    Ref,
    ref,
    watch,
} from "vue";
import ImageFallback from "../../components/imageFallback.js";
import type {
    LogChannel,
    LogEntry,
    LogUser,
} from "../../core/types.js";
import {
    createDiscordMarkdownParser,
    parseDiscordMarkdown,
} from "../../utils/markdown.js";
import {
    BanContextResult,
    loadBanContext,
} from "./ban/banContext.js";
import MessageLogContent from "./message/MessageLogContent.js";
import { parseMessageLogs } from "./message/messageParser.js";

type MemberMap = Ref<Map<string, unknown>>;
type ChannelMap = Ref<Map<string, unknown>>;

const EmptyBanContext: BanContextResult = {
    messages : [],
    members  : [],
    channels : [],
    command  : null,
};

function createMemberMap(members: readonly LogUser[], fallback: Map<string, unknown>): Map<string, LogUser> {
    const result = new Map<string, LogUser>();

    for (const [ id, member ] of fallback.entries())
        if (member)
            result.set(id, member as LogUser);

    for (const member of members)
        if (member?.id)
            result.set(member.id, member);

    return result;
}

function createChannelMap(channels: readonly LogChannel[], fallback: Map<string, unknown>): Map<string, LogChannel> {
    const result = new Map<string, LogChannel>();

    for (const [ id, channel ] of fallback.entries())
        if (channel)
            result.set(id, channel as LogChannel);

    for (const channel of channels)
        if (channel?.id)
            result.set(channel.id, channel);

    return result;
}

function formatLogTime(timestamp: number): string {
    return new Date(timestamp).toLocaleTimeString("fr-FR", {
        hour   : "2-digit",
        minute : "2-digit",
        second : "2-digit",
    });
}

function isBot(member: LogUser | undefined): boolean {
    return Boolean(member?.user && "bot" in member.user && member.user.bot === true);
}

function getDisplayName(member: LogUser): string {
    return member.serverNickname
        || member.nickname
        || member.displayName
        || member.user?.globalName
        || member.user?.username
        || member.id;
}

function getMemberAvatarUrl(member: LogUser): string {
    const serverAvatar = typeof member.serverAvatar === "string" ? member.serverAvatar : null;
    const avatar = typeof member.user?.avatar === "string" ? member.user.avatar : null;

    return serverAvatar
        || avatar
        || `https://cdn.discordapp.com/embed/avatars/${(BigInt(member.id) >> 22n) % 6n}.png`;
}

function getTag(member: LogUser): string {
    return member.user?.username || member.id;
}

function getBanActionLabels(log: LogEntry) {
    if (log.type === "member_ban_remove") {
        return {
            targetLabel     : "Membre débanni",
            emptyContext    : "Aucun message de contexte trouvé autour du débannissement.",
        };
    }

    return {
        targetLabel     : "Membre banni",
        emptyContext    : "Aucun message de contexte trouvé autour du bannissement.",
    };
}

export default defineComponent({
    props: {
        log: {
            type: Object as PropType<LogEntry>,
            required: true,
        },
        members: {
            type: Object as PropType<MemberMap>,
            required: true,
        },
        channels: {
            type: Object as PropType<ChannelMap>,
            required: true,
        },
    },

    setup(props) {
        const markdown = createDiscordMarkdownParser();
        const loading = ref(false);
        const error = ref<string | null>(null);
        const context = ref<BanContextResult>(EmptyBanContext);
        const expandedMessages = ref<Set<string>>(new Set());
        let activeController: AbortController | null = null;
        let requestSequence = 0;

        const members = computed(() => createMemberMap(context.value.members, props.members.value));
        const channels = computed(() => createChannelMap(context.value.channels, props.channels.value));
        const parsedMessages = computed(() => parseMessageLogs(context.value.messages, markdown, parseDiscordMarkdown));
        const auditExecutorIsBot = computed(() => isBot(props.log.executor ? members.value.get(props.log.executor) : undefined));
        const actionLabels = computed(() => getBanActionLabels(props.log));

        async function refreshContext() {
            activeController?.abort();
            activeController = null;
            requestSequence += 1;

            if (!props.log.executor || !auditExecutorIsBot.value) {
                loading.value = false;
                error.value = null;
                context.value = EmptyBanContext;
                expandedMessages.value = new Set();
                return;
            }

            const sequence = requestSequence;
            const controller = new AbortController();
            activeController = controller;
            loading.value = true;
            error.value = null;

            try {
                const nextContext = await loadBanContext(props.log, props.log.executor, {
                    signal: controller.signal,
                });

                if (sequence === requestSequence)
                    context.value = nextContext;
                if (sequence === requestSequence)
                    expandedMessages.value = new Set();
            } catch (cause) {
                if (controller.signal.aborted)
                    return;

                error.value = cause instanceof Error ? cause.message : "Impossible de charger le contexte.";
                context.value = EmptyBanContext;
            } finally {
                if (sequence === requestSequence) {
                    loading.value = false;
                    activeController = null;
                }
            }
        }

        watch(
            () => [
                props.log.id ?? `${props.log.type}:${props.log.createdAt}`,
                auditExecutorIsBot.value,
            ],
            refreshContext,
            { immediate: true },
        );

        onBeforeUnmount(() => {
            activeController?.abort();
        });

        function getMember(id: string | null | undefined): LogUser | undefined {
            return id ? members.value.get(id) : undefined;
        }

        function getMessageKey(log: LogEntry): string {
            return `${log.id ?? "no-id"}:${log.type}:${log.createdAt}`;
        }

        function toggleMessageExpansion(log: LogEntry) {
            const key = getMessageKey(log);
            const next = new Set(expandedMessages.value);

            if (next.has(key))
                next.delete(key);
            else
                next.add(key);

            expandedMessages.value = next;
        }

        function renderUser(id: string | null | undefined, label: string) {
            const member = getMember(id);
            const displayName = member ? getDisplayName(member) : id || "Inconnu";

            return (
                <div class="ban-preview-user-block">
                    <span>{label}</span>
                    {id
                        ? (
                            <router-link to={`/users/${id}`} class="ban-preview-user">
                                <ImageFallback
                                    src={member ? getMemberAvatarUrl(member) : undefined}
                                    default="https://cdn.discordapp.com/embed/avatars/0.png"
                                    alt={displayName}
                                />
                                <div>
                                    <strong>{displayName}</strong>
                                    <small>{member ? getTag(member) : id}</small>
                                </div>
                            </router-link>
                        )
                        : <div class="ban-preview-user ban-preview-user-missing">Inconnu</div>}
                </div>
            );
        }

        function renderMessage(log: LogEntry) {
            const member = getMember(log.executor);
            const channel = log.channel ? channels.value.get(log.channel) : null;
            const parsed = parsedMessages.value.find((message) => message.id === log.id && message.createdAt === log.createdAt);
            const isCommand = context.value.command?.log === log;
            const isExpanded = isCommand || expandedMessages.value.has(getMessageKey(log));
            const channelLabel = channel
                ? `#${channel.name}`
                : (log.channel ? `#${log.channel}` : "Salon inconnu");

            return (
                <div class={{
                    "ban-preview-message"         : true,
                    "ban-preview-message-command" : isCommand,
                }}>
                    <ImageFallback
                        src={member ? getMemberAvatarUrl(member) : undefined}
                        default="https://cdn.discordapp.com/embed/avatars/0.png"
                        alt={member ? getDisplayName(member) : "Utilisateur inconnu"}
                    />
                    <div class="ban-preview-message-body">
                        <div class="ban-preview-message-header">
                            <div class="ban-preview-message-identity">
                                <div class="ban-preview-message-meta">
                                    <strong>{member ? getDisplayName(member) : log.executor || "Utilisateur inconnu"}</strong>
                                    {isBot(member) && <span>Bot</span>}
                                    {isCommand && <span>Commande détectée</span>}
                                    <small>{formatLogTime(log.createdAt)}</small>
                                </div>
                                <div class="ban-preview-message-channel">
                                    <span class="material-symbols-outlined">tag</span>
                                    <small>{channelLabel}</small>
                                </div>
                            </div>

                            {!isCommand && (
                                <button
                                    type="button"
                                    class="ban-preview-message-expand"
                                    aria-expanded={isExpanded}
                                    title={isExpanded ? "Masquer le message" : "Afficher le message"}
                                    onClick={() => toggleMessageExpansion(log)}
                                >
                                    <span class="material-symbols-outlined">
                                        {isExpanded ? "expand_less" : "expand_more"}
                                    </span>
                                </button>
                            )}
                        </div>

                        {isExpanded && (
                            <div class="ban-preview-message-content">
                                {parsed
                                    ? <MessageLogContent log={parsed} />
                                    : "Message vide"}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        function renderContext() {
            if (loading.value)
                return (
                    <div class="ban-preview-loading">
                        <span class="ban-preview-loading-spinner" aria-hidden="true" />
                        <span>Chargement du contexte commande…</span>
                    </div>
                );

            if (error.value)
                return <p class="ban-preview-empty">{error.value}</p>;

            if (!context.value.messages.length)
                return <p class="ban-preview-empty">{actionLabels.value.emptyContext}</p>;

            return (
                <div class="ban-preview-message-list">
                    {context.value.messages.map(renderMessage)}
                </div>
            );
        }

        function renderCommandExecutorCard() {
            const command = context.value.command;

            if (!command?.log.executor)
                return null;

            const executor = getMember(command.log.executor);
            const displayName = executor ? getDisplayName(executor) : command.log.executor;

            return (
                <router-link to={`/users/${command.log.executor}`} class="ban-preview-command-summary">
                    <ImageFallback
                        src={executor ? getMemberAvatarUrl(executor) : undefined}
                        default="https://cdn.discordapp.com/embed/avatars/0.png"
                        alt={displayName}
                    />
                    <div>
                        <span>Exécutant probable</span>
                        <strong>{displayName}</strong>
                        <small>
                            {executor ? getTag(executor) : command.log.executor}
                            {" · "}
                            {command.pattern} à {formatLogTime(command.log.createdAt)}
                        </small>
                    </div>
                </router-link>
            );
        }

        return () => (
            <div class="ban-preview">
                <div class="ban-preview-summary">
                    {renderUser(props.log.target, actionLabels.value.targetLabel)}
                    {renderUser(props.log.executor, "Exécutant audit")}
                </div>

                {renderCommandExecutorCard()}

                {typeof props.log.extra?.reason === "string" && props.log.extra.reason && (
                    <div class="ban-preview-card">
                        <h3>Raison audit</h3>
                        <p>{props.log.extra.reason}</p>
                    </div>
                )}

                {auditExecutorIsBot.value && (
                    <div class="ban-preview-card">
                        <h3>Contexte commande</h3>
                        {renderContext()}
                    </div>
                )}
            </div>
        );
    },
});
